import { Button, ButtonWithLoader } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import type { ApiResponse } from '@shared/types'
import { useQueryClient } from '@tanstack/react-query'
import axios, { type AxiosError } from 'axios'
import { CreditCard, Landmark } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  useAccountDetailsQuery,
  useBillingAddressQuery,
  useBillingCardsQuery,
  useBillingRechargeDetailsQuery,
  useCashfreeCreateOrderMutation,
  useConversionRate,
} from 'v2/pages/billing/queries'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { billingQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RCard } from 'v2/types/api/billing'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { logger } from 'v2/utils/logger'
import { errorToast, successToast } from 'v2/utils/toasts'
import { Currency } from '../billing.config'
import { AddCardModal } from '../components/add-card-modal'
import { AddFirstCard } from '../components/add-first-card'
import { CardSelector } from '../components/card-selector'
import { MethodTile } from '../components/method-tile'
import { RechargeAmountTile } from '../components/recharge-amount-tile'
import { shouldUseFirstCardWizard } from '../first-card-wizard'
import { useRechargeMutation } from '../mutations'
import {
  buildPaymentCallbackUrl,
  getBankRailDisabledReason,
  getCardFeeLabel,
  getSortedRechargeChoices,
  launchCashfreeHostedCheckout,
  ZERO_FEE_LABEL,
} from '../recharge-shared'
import { refreshBillingAfterPayment } from '../refresh-billing-queries'
import { CardState } from '../types'
import { buildUpgradePlanPatch } from '../upgrade-plan'
import { useIsCashfreeProvider } from '../use-billing-provider'
import { getAccountId, pollForPlanUpdate, trackPaymentFailed } from '../utils'
import { resolveRechargeRoute } from './recharge-route'
import {
  isRazorpaySavedCard,
  rechargeGatewayLabel,
  useRazorpayStandardCheckout,
} from './use-razorpay-standard-checkout'

type Method = 'card' | 'netbanking'

const getPaymentErrorDescription = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return getErrorText(error as AxiosError<ApiResponse<Response>>) || 'Could not initiate payment.'
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'Could not initiate payment.'
}

const getRechargeDisabledReason = ({
  isLoading,
  selectedAmount,
  method,
  selectedCard,
  hasActiveCards,
  bankRailsDisabledReason,
}: {
  isLoading: boolean
  selectedAmount: number
  method: Method
  selectedCard: number
  hasActiveCards: boolean
  bankRailsDisabledReason?: string
}): string | undefined => {
  if (isLoading) return undefined
  if (!selectedAmount) return 'Select a recharge amount'
  if (method === 'card' && !hasActiveCards) return 'Add a card first'
  if (method === 'card' && !selectedCard) return 'Select a card'
  if (method === 'netbanking' && bankRailsDisabledReason) return bankRailsDisabledReason
  return undefined
}

interface RechargeModalProps {
  isOpen: boolean
  onClose: () => void
  cards?: RCard[]
  isTrial?: boolean
  onUpgrade?: (cardMappingId: number, amount: number) => Promise<unknown>
}

export const RechargeModal = ({
  isOpen,
  onClose,
  cards = [],
  isTrial = false,
  onUpgrade,
}: RechargeModalProps) => {
  // The provider's client, not the rq-setup singleton: several test files
  // mock.module that singleton with a 2-3 method stub and those mocks leak
  // process-globally (AGENTS.md failure mode 1). In the app they are the same
  // object.
  const queryClient = useQueryClient()
  const { data: profile, refetch: refetchProfile } = useProfileQuery()
  const accountId = getAccountId()
  const { data: paymentCards } = useBillingCardsQuery()
  const { data: rechargeDetails } = useBillingRechargeDetailsQuery()
  const { data: billingInformation } = useBillingAddressQuery()
  const { data: accountDetails } = useAccountDetailsQuery()
  const { mutate: recharge, isPending } = useRechargeMutation()
  const { mutateAsync: createCashfreeOrder, isPending: creatingCashfreeOrder } =
    useCashfreeCreateOrderMutation()
  const { isCashFreeTesting } = useFeatureFlags()
  const isCashfreeProvider = useIsCashfreeProvider()
  const sortedRechargeChoices = getSortedRechargeChoices(rechargeDetails?.recharge_choices)
  const { conversionRate } = useConversionRate()
  const [selectedAmount, setSelectedAmount] = useState<number>(sortedRechargeChoices?.[0] ?? 0)
  const [selectedCard, setSelectedCard] = useState<number>(
    cards.find((card) => card.default)?.card_mapping_id ?? cards[0]?.card_mapping_id ?? 0,
  )
  // Netbanking is shown for INR (Cashfree-backed) accounts.
  const isIndiaCurrency = accountDetails?.account?.currency === Currency.INR
  const showNetbankingPicker = isIndiaCurrency
  // Netbanking is the only bank rail on this modal, and it is CASHFREE-backed
  // (see launchCashfreeHostedCheckout below) — so it carries Cashfree's ₹10,000
  // merchant-account floor (SER-5416).
  const bankRailsDisabledReason = getBankRailDisabledReason(
    'netbanking',
    selectedAmount,
    accountDetails?.account?.currency ?? '',
    isCashFreeTesting,
  )
  const [method, setMethod] = useState<Method>('card')
  const [isLaunchingNetbanking, setIsLaunchingNetbanking] = useState(false)
  const activeCards = cards.filter((card) => card.state === CardState.ACTIVE)
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false)
  const [isUpgrading, setIsUpgrading] = useState(false)
  const isSubmittingRef = useRef(false)
  useEffect(() => {
    setSelectedAmount(sortedRechargeChoices?.[0] ?? 0)
    setSelectedCard(
      activeCards.find((card) => card.default)?.card_mapping_id ?? cards[0]?.card_mapping_id ?? 0,
    )
  }, [rechargeDetails, cards])

  const handleClose = () => {
    setSelectedAmount(sortedRechargeChoices?.[0] ?? 0)
    setSelectedCard(
      cards.find((card) => card.default)?.card_mapping_id ?? cards[0]?.card_mapping_id ?? 0,
    )
    setMethod('card')
    setIsLaunchingNetbanking(false)
    isSubmittingRef.current = false
    setIsUpgrading(false)
    onClose()
  }

  // A RAZORPAY-client saved card must open Razorpay Standard Checkout regardless of
  // which gateway this modal itself belongs to. Detect their
  // presence to warm the SDK; handleRecharge routes to Standard Checkout for them.
  const hasRazorpayCard = activeCards.some(isRazorpaySavedCard)
  const {
    launchStandardCheckout: launchRazorpayStandardCheckout,
    isLaunching: isRazorpayLaunching,
    isCreatingOrder: isRazorpayCreatingOrder,
  } = useRazorpayStandardCheckout({
    onClose: handleClose,
    warm: isOpen && hasRazorpayCard,
    isTrial,
  })

  const isLoading =
    isPending ||
    isUpgrading ||
    creatingCashfreeOrder ||
    isLaunchingNetbanking ||
    isRazorpayLaunching ||
    isRazorpayCreatingOrder

  const handleNetbankingPay = async () => {
    if (isLoading || !selectedAmount || bankRailsDisabledReason) return
    try {
      // Trial netbanking is an upgrade (same gate as the card path); netbanking
      // is async, so the plan flip rides on the order's upgrade_plan. One shared
      // helper with every Razorpay path so the gate has a single spelling.
      const order = await createCashfreeOrder({
        account_id: accountId,
        amount: selectedAmount,
        product_type: 'plivo',
        payment_method_type: 'netbanking',
        return_url: buildPaymentCallbackUrl('netbanking'),
        ...buildUpgradePlanPatch(isTrial),
      })
      if (!order.payment_session_id) {
        // Friendly message — bubbles up through getPaymentErrorDescription
        // into the toast. Sentinel for logs only.
        logger.error('recharge-modal: order missing payment_session_id', { order })
        throw new Error('Could not start payment. Please try again.')
      }
      setIsLaunchingNetbanking(true)
      await launchCashfreeHostedCheckout(order.payment_session_id)
    } catch (error) {
      errorToast({ title: 'Payment Failed', description: getPaymentErrorDescription(error) })
      setIsLaunchingNetbanking(false)
      // Tracking last so a throw here can't skip the loading-state reset above.
      trackPaymentFailed('recharge', error, {
        amount: selectedAmount,
        currency: rechargeDetails?.currency ?? 'USD',
        method: 'netbanking',
      })
    }
  }

  const handleUpgradeClick = async () => {
    if (!onUpgrade || isSubmittingRef.current) return
    isSubmittingRef.current = true
    setIsUpgrading(true)
    try {
      const result = await onUpgrade(selectedCard, selectedAmount)
      const response = result as {
        data?: { redirection_url?: string; plan_status?: string }
      }
      if (response?.data?.redirection_url) {
        window.location.href = response.data.redirection_url
        return
      }
      if (response?.data?.plan_status === 'failed') {
        errorToast({
          title: 'Upgrade Failed',
          description: 'Payment was processed but plan upgrade failed. Please contact support.',
        })
        handleClose()
        return
      }

      const updated = await pollForPlanUpdate(refetchProfile, profile?.plan || 'free_trial')
      handleClose()
      successToast({
        title: 'Plan Upgraded',
        description: updated
          ? 'Your plan has been upgraded successfully'
          : 'Your plan upgrade is being processed. It may take a few moments to reflect.',
      })
    } catch (error) {
      const axiosError = error as AxiosError<ApiResponse<Response>>
      errorToast({
        title: 'Upgrade Failed',
        description: getErrorText(axiosError) || 'Upgrade failed. Please try again.',
      })
      isSubmittingRef.current = false
      setIsUpgrading(false)
    }
  }

  const handleRecharge = async () => {
    // Resolve the selected card before the initiation event: a RAZORPAY-client card
    // routes to Standard Checkout below, so the funnel has to be labelled with that
    // gateway rather than the account-level provider.
    const selectedCardObj = activeCards.find((card) => card.card_mapping_id === selectedCard)
    track(
      AnalyticsEvents.RECHARGE_INITIATED,
      {
        amount: selectedAmount,
        currency: rechargeDetails?.currency ?? 'USD',
        method,
        is_trial: isTrial,
        gateway: rechargeGatewayLabel(selectedCardObj, isCashfreeProvider),
      },
      // Netbanking + card-3DS both end in a _self redirect below; sendBeacon
      // guarantees delivery across the unload (pattern: psp-redirect.ts).
      { transport: 'sendBeacon' },
    )
    // The precedence lives in recharge-route.ts, where it is unit-tested: gateway
    // capability outranks the trial shortcut, because a Razorpay-vaulted token is
    // chargeable on exactly one rail. See that module for why.
    const route = resolveRechargeRoute({
      method,
      isRazorpayCard: isRazorpaySavedCard(selectedCardObj),
      isTrial,
      hasUpgradeHandler: Boolean(onUpgrade),
    })

    // Netbanking diverges from the Stripe recharge path entirely.
    if (route === 'netbanking') {
      return handleNetbankingPay()
    }
    if (isLoading) return

    // Hosted, PCI-safe Razorpay Standard Checkout instead of the server-side
    // /recharge/:card_mapping_id charge — independent of this modal's own gateway.
    // The hook emits CREDITS_RECHARGED on success, standing in for the /recharge
    // success callback this return skips, and carries upgrade_plan on the order so
    // a trial account's plan still flips (on the capture webhook).
    if (route === 'razorpay-checkout') {
      await launchRazorpayStandardCheckout(selectedAmount)
      return
    }

    // Stripe/Cashfree card on a trial account: /change-plan charges and upgrades
    // synchronously, so it needs no upgrade_plan round-trip.
    if (route === 'change-plan') {
      return handleUpgradeClick()
    }

    recharge(
      {
        account_id: accountId,
        card_mapping_id: selectedCard,
        amount: selectedAmount.toString(),
        // Defensive symmetry with the identical /recharge call in
        // razorpay-custom-recharge-modal: this branch is only reached by a trial
        // account if a host ever renders this modal WITHOUT onUpgrade, and then
        // /change-plan is unavailable so the order's upgrade_plan is the only
        // mechanism left. Every host passes onUpgrade today, so this is latent —
        // but an omitted spread here is precisely the drift the helper exists to
        // stop.
        ...buildUpgradePlanPatch(isTrial),
      },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({
            queryKey: billingQueryKeys().rechargeDetails(accountId, conversionRate),
          })
          handleClose()
          // 3DS redirects are pending bank verification — not a completed recharge.
          // Refresh only AFTER this check: starting a poll and then navigating
          // away in the same tick throws the poll away with the page.
          if (data.redirection_url) {
            window.location.href = data.redirection_url
            return
          }
          // Polls until the credit lands, and reaches the balance/transactions
          // queries by prefix — the exact-key form this replaces missed the
          // Receipts pager (any offset beyond the first page).
          void refreshBillingAfterPayment(queryClient, accountId)
          track(AnalyticsEvents.CREDITS_RECHARGED, {
            amount: selectedAmount,
            currency: rechargeDetails?.currency ?? 'USD',
            is_trial: isTrial,
          })
          successToast({
            title: 'Success',
            description: 'Recharge successful',
          })
        },
        onError: (error) => {
          trackPaymentFailed('recharge', error, {
            amount: selectedAmount,
            currency: rechargeDetails?.currency ?? 'USD',
          })
          const axiosError = error as AxiosError<ApiResponse<Response>>
          let errorText = `${getErrorText(axiosError)}`.trim()

          // Replace href links with cx path for 403 errors
          if (axiosError.response?.status === 403) {
            errorText = errorText.replace(
              /href=['"]https?:\/\/[^'"]*['"]/g,
              "href='/billing/payment-methods#auto-recharge'",
            )
          }
          errorToast({
            title: 'Failed',
            description: errorText || 'Recharge failed',
          })
        },
      },
    )
  }

  const handleAddCard = () => {
    setIsAddCardModalOpen(true)
  }
  return (
    <>
      {/* The first-card WIZARD is a US address form whose card step posts to
          STRIPE, so it is gated on a resolved non-INR currency — an INR org goes to
          AddCardModal, which routes to Razorpay. Without that conjunct a brand-new
          Indian account (no cards AND no billing address) got a Stripe card form.
          Same predicate as settings/payment-modes; see ../first-card-wizard.ts. */}
      {isAddCardModalOpen &&
        (shouldUseFirstCardWizard({
          accountCurrency: accountDetails?.account?.currency,
          cardCount: paymentCards?.length ?? 0,
          hasBillingZipcode: Boolean(billingInformation?.zipcode),
        }) ? (
          <AddFirstCard isOpen={isAddCardModalOpen} onClose={() => setIsAddCardModalOpen(false)} />
        ) : (
          <AddCardModal isOpen={isAddCardModalOpen} onClose={() => setIsAddCardModalOpen(false)} />
        ))}
      <Dialog
        open={isOpen && !isAddCardModalOpen}
        // Non-modal while Razorpay's hosted checkout is open so its <body> iframe
        // isn't trapped in Radix's inert region; block the user-initiated close so
        // the modal doesn't disappear out from under the open checkout.
        modal={!isRazorpayLaunching}
        onOpenChange={() => {
          if (isRazorpayLaunching) return
          handleClose()
        }}
      >
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Credits</DialogTitle>
          </DialogHeader>

          <div className="gap-4 flex flex-col">
            <RechargeAmountTile
              title="Select Amount"
              selectedAmount={selectedAmount}
              onSelect={setSelectedAmount}
              rechargeChoicesData={{
                choicesList: sortedRechargeChoices || [],
                currency: rechargeDetails?.currency || 'USD',
              }}
              disabled={isLoading}
            />

            <div>
              <h4 className="text-p-600 mb-3">Select Payment Mode</h4>
              {showNetbankingPicker ? (
                <div className="flex flex-col gap-2">
                  <MethodTile
                    method="card"
                    selected={method}
                    onSelect={setMethod}
                    icon={<CreditCard className="size-5" />}
                    title="Card"
                    subtitle="Pay using credit or debit card"
                    badge={getCardFeeLabel(rechargeDetails?.gateway_fee_rate)}
                    disabled={isLoading}
                  />
                  <MethodTile
                    method="netbanking"
                    selected={method}
                    onSelect={setMethod}
                    icon={<Landmark className="size-5" />}
                    title="Netbanking"
                    subtitle={bankRailsDisabledReason ?? "Pay using your bank's net banking"}
                    badge={ZERO_FEE_LABEL}
                    disabled={isLoading || bankRailsDisabledReason !== undefined}
                  />
                </div>
              ) : (
                rechargeDetails?.gateway_fee_rate !== undefined && (
                  <div className="text-xs text-secondary mb-3">
                    Payment gateway fee of {rechargeDetails.gateway_fee_rate}% would be charged
                  </div>
                )
              )}
              {method === 'card' && (
                <div className={showNetbankingPicker ? 'mt-3' : ''}>
                  <CardSelector
                    activeCards={activeCards}
                    selectedCard={selectedCard}
                    onCardChange={setSelectedCard}
                    onAddCard={handleAddCard}
                    isPending={isLoading}
                    showAddCardButton={true}
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button
              variant="outline"
              // Block Cancel ONLY while a Razorpay Standard Checkout is being
              // prepared/opened — handleClose is unguarded and the onOpenChange
              // guard only covers ESC/overlay dismiss, not this click, so without
              // this the host could close out from under the open checkout (or
              // launch it after close). Scoped to the Razorpay states so the
              // Cashfree/Stripe recharge Cancel behaviour is unchanged.
              disabled={isRazorpayLaunching || isRazorpayCreatingOrder}
              onClick={handleClose}
              data-testid="billing-recharge-cancel-btn"
            >
              Cancel
            </Button>
            <div className="flex ml-auto">
              <ButtonWithLoader
                type="submit"
                // Card branch needs a selected card; netbanking only needs an
                // amount (the Cashfree-hosted page handles the bank picker).
                disabled={
                  isLoading ||
                  !selectedAmount ||
                  (method === 'card' && (!selectedCard || !activeCards.length)) ||
                  (method === 'netbanking' && bankRailsDisabledReason !== undefined)
                }
                disabledReason={getRechargeDisabledReason({
                  isLoading,
                  selectedAmount,
                  method,
                  selectedCard,
                  hasActiveCards: activeCards.length > 0,
                  bankRailsDisabledReason,
                })}
                className="ml-2"
                loading={isLoading}
                data-testid={isTrial ? 'add-credits-button' : 'recharge-button'}
                onClick={() => handleRecharge()}
              >
                {isTrial ? 'Add Credits' : 'Recharge'}
              </ButtonWithLoader>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
